'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { mapObject, debounce } from 'underscore';
import { KEYS, MODIFIERS } from 'DesignData/enums/Keys';
import PaneSize from 'DesignData/models/PaneSize';
import Keybinding from 'DesignData/models/Keybinding';
import { SIDEBAR_WIDTH } from 'DesignData/constants/PaneSizes';
import { SIDEBAR, CODE_PANES } from 'DesignData/enums/CustomWidgetEditorPaneTypes';
import { PIXELS } from 'DesignData/enums/SizeUnits';
import ResizablePanes from 'DesignComponents/resizablePanes/ResizablePanes';
import Pane from 'DesignComponents/resizablePanes/Pane';
import CodePanesContainer from 'DesignEditorsUI/containers/customWidgetEditor/CodePanes';
import ToolbarContainer from 'DesignEditorsUI/containers/customWidgetEditor/Toolbar';
import SidebarContainer from 'DesignEditorsUI/containers/customWidgetEditor/Sidebar';
import GlobalContentCard from 'DesignEditorsUI/components/customWidgetEditor/GlobalContentCard';
import EditorWrapper from 'DesignEditorsUI/components/shared/EditorWrapper';
import FullScreenFieldEditor from 'DesignEditorsUI/components/customWidgetEditor/FullScreenFieldEditor';
import LanguageEditor from 'DesignEditorsUI/components/customWidgetEditor/LanguageEditor';

class CustomWidgetEditor extends PureComponent {
  constructor(...args) {
    super(...args);
    this.keybindingConfig = {
      [MODIFIERS.COMMAND]: {
        [KEYS.S]: new Keybinding({
          handler: this.props.onSave
        }),
        [KEYS.Z]: new Keybinding({
          handler: this.props.onUndoCustomWidgetEdit
        }),
        [KEYS.Y]: new Keybinding({
          handler: this.props.onRedoCustomWidgetEdit
        })
      },
      [MODIFIERS.COMMAND_SHIFT]: {
        [KEYS.Z]: new Keybinding({
          handler: this.props.onRedoCustomWidgetEdit
        }),
        [KEYS.P]: new Keybinding({
          handler: this.props.onPublishCustomWidget
        })
      }
    };
    this.onCodePanesRender = debounce(() => {
      this.props.editorLoaded(this.props.customWidgetId);
    }, 250);

    this.onSidebarResize = sizesById => {
      const paneOptions = mapObject(sizesById, size => {
        return {
          size
        };
      });
      this.props.updatePaneOptions(paneOptions);
    };
  }

  renderToolbar() {
    const {
      customWidgetId,
      revisionId
    } = this.props;
    return /*#__PURE__*/_jsx(ToolbarContainer, {
      customWidgetId: customWidgetId,
      revisionId: revisionId
    });
  }

  renderEditor() {
    const {
      customWidgetId,
      paneOptions,
      revisionId,
      isEditingGlobalContent,
      isFullScreenEditorOpen,
      selectedLanguageEditor
    } = this.props;
    const {
      size: codePanesSize
    } = paneOptions[CODE_PANES] || new PaneSize({});
    const codePanesClasses = !(isEditingGlobalContent || isFullScreenEditorOpen) ? 'code-panes-pane' : "";

    let renderedCodePanesContent = /*#__PURE__*/_jsx(CodePanesContainer, {
      customWidgetId: customWidgetId,
      revisionId: revisionId,
      onCodePanesRender: this.onCodePanesRender
    });

    if (isEditingGlobalContent) {
      renderedCodePanesContent = /*#__PURE__*/_jsx(GlobalContentCard, {
        customWidgetId: customWidgetId,
        revisionId: revisionId
      });
    } else if (isFullScreenEditorOpen) {
      renderedCodePanesContent = /*#__PURE__*/_jsx(FullScreenFieldEditor, {
        customWidgetId: customWidgetId,
        revisionId: revisionId
      });
    } else if (selectedLanguageEditor) {
      renderedCodePanesContent = /*#__PURE__*/_jsx(LanguageEditor, {
        customWidgetId: customWidgetId,
        revisionId: revisionId,
        selectedLanguageEditor: selectedLanguageEditor
      });
    }

    const {
      size: sidebarSize
    } = paneOptions[SIDEBAR] || new PaneSize({});
    const minSidebarSize = new PaneSize({
      value: SIDEBAR_WIDTH
    });
    return /*#__PURE__*/_jsxs(ResizablePanes, {
      direction: "vertical",
      onPaneResize: this.onSidebarResize,
      children: [/*#__PURE__*/_jsx(Pane, {
        className: codePanesClasses,
        defaultSize: codePanesSize,
        paneId: CODE_PANES,
        scrollable: false,
        children: renderedCodePanesContent
      }), /*#__PURE__*/_jsx(Pane, {
        unitsOnResize: PIXELS,
        defaultSize: sidebarSize,
        minSize: minSidebarSize,
        paneId: SIDEBAR,
        scrollable: false,
        children: /*#__PURE__*/_jsx(SidebarContainer, {
          customWidgetId: customWidgetId,
          revisionId: revisionId
        })
      })]
    });
  }

  render() {
    return /*#__PURE__*/_jsxs(EditorWrapper, {
      className: "custom-widget-editor",
      keybindingConfig: this.keybindingConfig,
      children: [this.renderToolbar(), this.renderEditor()]
    });
  }

}

CustomWidgetEditor.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  isEditingGlobalContent: PropTypes.bool.isRequired,
  revisionId: PropTypes.number,
  onRedoCustomWidgetEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUndoCustomWidgetEdit: PropTypes.func.isRequired,
  paneOptions: PropTypes.object.isRequired,
  updatePaneOptions: PropTypes.func.isRequired,
  isFullScreenEditorOpen: PropTypes.bool.isRequired,
  selectedLanguageEditor: PropTypes.string,
  onPublishCustomWidget: PropTypes.func.isRequired,
  editorLoaded: PropTypes.func.isRequired
};
export default CustomWidgetEditor;